import React from 'react';
import PropTypes from 'prop-types';
import { FONT, COLOURS } from '../../constants/theme';

/**
 * ErrorBoundary — catches unhandled render errors anywhere in the
 * component tree and renders a user-friendly fallback instead of a
 * blank screen.
 *
 * Usage: wrap <App /> (or any subtree) with <ErrorBoundary />.
 * Must be a class component — hook equivalents don't exist for
 * componentDidCatch as of React 18.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message ?? 'Unknown error' };
  }

  componentDidCatch(error, info) {
    // Replace with your error reporting service (Sentry, Datadog, etc.)
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          fontFamily: FONT,
          background: COLOURS.cream,
          border: `4px solid ${COLOURS.primary}`,
          padding: 24,
          margin: 16,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 10, color: COLOURS.primary, marginBottom: 12 }}>
          ✕ SOMETHING WENT WRONG
        </div>
        <div style={{ fontSize: 7, color: COLOURS.textMid, lineHeight: 2, marginBottom: 16 }}>
          {this.state.message}
        </div>
        <button
          style={{
            fontFamily: FONT,
            fontSize: 7,
            color: COLOURS.textInverted,
            background: COLOURS.primary,
            border: `3px solid ${COLOURS.primaryDark}`,
            padding: '8px 16px',
            cursor: 'pointer',
          }}
          onClick={() => this.setState({ hasError: false, message: '' })}
        >
          TRY AGAIN
        </button>
      </div>
    );
  }
}

ErrorBoundary.propTypes = {
  /** Child component tree to wrap. */
  children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
